<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-1">
      <div class="text-dark font-size-18 font-weight-bold">
        Fee-Wise Collection & Due
      </div>
      <div class="small text-muted" v-if="selectedSession">
        Session: <b>{{ selectedSession.name }}</b>
      </div>
    </div>
    <div class="text-gray-6 mb-3">Comparison of Paid vs Outstanding dues by Fee Category</div>
    <div class="chart-container" style="position: relative; height: 340px; width: 100%;">
      <canvas ref="myChart"></canvas>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js'
import ChartJSPluginDatalabels from 'chartjs-plugin-datalabels'

function formatCompactIndian(num) {
  if (!num || isNaN(num)) return '0'
  if (num >= 10000000) {
    const cr = num / 10000000
    return (cr % 1 === 0 ? cr : cr.toFixed(2)) + 'Cr'
  }
  if (num >= 100000) {
    const lakh = num / 100000
    return (lakh % 1 === 0 ? lakh : lakh.toFixed(2)) + 'L'
  }
  if (num >= 1000) {
    const k = num / 1000
    return (k % 1 === 0 ? k : k.toFixed(1)) + 'k'
  }
  return num.toString()
}

export default {
  name: 'FeeWiseDueChart',
  props: {
    reportData: Object,
    selectedSession: Object,
  },
  data: function () {
    return {
      chartInstance: null,
    }
  },
  watch: {
    reportData: {
      immediate: true,
      handler(reportDataLocal) {
        if (!reportDataLocal || !reportDataLocal.fee_wise_data) return
        this.$nextTick(() => {
          this.renderChart(reportDataLocal.fee_wise_data)
        })
      },
    },
  },
  methods: {
    formatCompactIndian,
    renderChart(feeWiseReportData) {
      if (this.chartInstance) {
        this.chartInstance.destroy()
      }
      if (!this.$refs.myChart || !feeWiseReportData || !feeWiseReportData.length) return

      const feeNames = feeWiseReportData.map((repData) => repData.fee ? repData.fee.name : 'Unknown')
      const paidData = feeWiseReportData.map((repData) => repData.feewise_total_paid_amount || 0)
      const unpaidData = feeWiseReportData.map((repData) => repData.feewise_total_unpaid_amount || 0)

      this.chartInstance = new Chart(this.$refs.myChart, {
        type: 'bar',
        data: {
          labels: feeNames,
          datasets: [
            {
              label: 'Paid',
              backgroundColor: '#1890ff',
              hoverBackgroundColor: '#40a9ff',
              data: paidData,
              maxBarThickness: 45,
              minBarLength: 8, // Guarantees small amounts (e.g. ₹3,000) remain visually visible!
              datalabels: {
                display: false,
              },
            },
            {
              label: 'Due',
              backgroundColor: '#ff4d4f',
              hoverBackgroundColor: '#ff7875',
              data: unpaidData,
              maxBarThickness: 45,
              minBarLength: 8, // Guarantees small amounts (e.g. ₹3,000) remain visually visible!
              datalabels: {
                display: false,
              },
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            position: 'top',
            align: 'end',
            labels: {
              usePointStyle: true,
              fontSize: 12,
            },
          },
          tooltips: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: (tooltipItem, data) => {
                const datasetLabel = data.datasets[tooltipItem.datasetIndex].label || ''
                const val = tooltipItem.yLabel || 0
                return `${datasetLabel}: ₹${val.toLocaleString('en-IN')}`
              },
              footer: (tooltipItems) => {
                let total = 0
                tooltipItems.forEach((item) => {
                  total += item.yLabel || 0
                })
                return `Total Fee: ₹${total.toLocaleString('en-IN')}`
              },
            },
          },
          scales: {
            xAxes: [
              {
                stacked: true,
                gridLines: { display: false },
                ticks: {
                  fontSize: 11,
                  fontColor: '#555',
                },
              },
            ],
            yAxes: [
              {
                stacked: true,
                gridLines: { color: '#f0f0f0' },
                ticks: {
                  beginAtZero: true,
                  callback: (value) => '₹' + formatCompactIndian(value),
                  fontSize: 11,
                  fontColor: '#888',
                },
              },
            ],
          },
        },
        plugins: [ChartJSPluginDatalabels],
      })
    },
  },
  beforeDestroy() {
    if (this.chartInstance) {
      this.chartInstance.destroy()
    }
  },
}
</script>
